import type { PostProcessStep } from "lib/consts/postProcessRules";
import { summarizeStep } from "lib/consts/postProcessRules";

const RuleToggle = ({
    enabled,
    onToggle,
    disabled,
    label,
}: {
    enabled: boolean;
    onToggle: () => void;
    disabled?: boolean;
    label: string;
}) => (
    <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-label={label}
        disabled={disabled}
        onClick={onToggle}
        className={`inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border p-0.5 transition-colors duration-200 ease-in-out disabled:cursor-not-allowed disabled:opacity-50 ${
            enabled
                ? "justify-end border-primary2 bg-primary2"
                : "justify-start border-black70 bg-black80"
        }`}
    >
        <span className="pointer-events-none block h-4 w-4 shrink-0 rounded-full bg-white shadow" />
    </button>
);

type PostProcessRuleCardProps = {
    step: PostProcessStep;
    index: number;
    isBusy?: boolean;
    onEdit: (step: PostProcessStep) => void;
    onDelete: (step: PostProcessStep) => void;
    onToggleEnabled: (step: PostProcessStep) => void;
};

const PostProcessRuleCard = ({
    step,
    index,
    isBusy,
    onEdit,
    onDelete,
    onToggleEnabled,
}: PostProcessRuleCardProps) => {
    const title = step.name?.trim() || `Rule ${index + 1}`;
    const summary = summarizeStep(step);

    return (
        <div className="flex w-full flex-col gap-2 rounded-r5 border border-black70 bg-black90 p-3">
            <div className="flex items-start justify-between gap-3">
                <p className="min-w-0 flex-1 truncate text-h5 font-semibold text-white">{title}</p>
                <div className="flex shrink-0 items-center gap-2">
                    <RuleToggle
                        enabled={step.enabled !== false}
                        disabled={isBusy}
                        label={`Toggle ${title}`}
                        onToggle={() => onToggleEnabled(step)}
                    />
                    <button
                        type="button"
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-r5 border border-black70 bg-black90 text-h5 text-white hover:bg-black80"
                        onClick={() => onEdit(step)}
                        disabled={isBusy}
                        aria-label={`Edit ${title}`}
                    >
                        ✎
                    </button>
                    <button
                        type="button"
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-r5 border border-black70 bg-black90 text-h5 text-white hover:bg-black80"
                        onClick={() => onDelete(step)}
                        disabled={isBusy}
                        aria-label={`Delete ${title}`}
                    >
                        ×
                    </button>
                </div>
            </div>
            <p className="min-w-0 text-h5 text-black40 line-clamp-2">{summary}</p>
        </div>
    );
};

export default PostProcessRuleCard;
